import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export interface WasapiDevice {
  id: string;
  name: string;
  maxChannels: number;
  isDefault: boolean;
}

export interface RecordingStatus {
  isRecording: boolean;
  deviceId?: string;
  channels: number;
  bytesRecorded: number;
  duration: { hours: number; minutes: number; seconds: number };
  status: string;
}

export interface WasapiCommand {
  action: string;
  deviceId?: string;
  channels?: number;
  sampleRate?: number;
  outputPath?: string;
  resample?: boolean;
  token?: string;
}

export interface WasapiResponse {
  type: string;
  data?: any;
  success: boolean;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class WasapiRecorderService {
  private socket: WebSocket | null = null;
  private readonly WS_URL = 'ws://127.0.0.1:58080/audio';
  private readonly AUTH_TOKEN = 'viq-audio-capture-2025';
  private audioDataChunkCount = 0; // Track audio data messages

  private connectionStatus = new BehaviorSubject<boolean>(false);
  private audioDataSubject = new Subject<ArrayBuffer>();
  private devicesSubject = new BehaviorSubject<WasapiDevice[]>([]);
  private recordingStatusSubject = new BehaviorSubject<RecordingStatus | null>(null);
  private errorSubject = new Subject<string>();

  public connectionStatus$ = this.connectionStatus.asObservable();
  public audioData$ = this.audioDataSubject.asObservable();
  public devices$ = this.devicesSubject.asObservable();
  public recordingStatus$ = this.recordingStatusSubject.asObservable();
  public error$ = this.errorSubject.asObservable();

  constructor() { }

  /**
   * Check if the WASAPI service is installed and running
   */
  async checkIfInstalled(): Promise<boolean> {
    return new Promise((resolve) => {
      const testSocket = new WebSocket(this.WS_URL);
      let resolved = false;
      
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          testSocket.close();
          resolve(false);
        }
      }, 3000);

      testSocket.onopen = () => {
        // Wait briefly for the server's initial message to avoid interrupting the send
        setTimeout(() => {
          if (!resolved) {
            resolved = true;
            clearTimeout(timeout);
            testSocket.close();
            resolve(true);
          }
        }, 100);
      };

      testSocket.onerror = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          resolve(false);
        }
      };

      testSocket.onclose = () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeout);
          resolve(false);
        }
      };
    });
  }

  /**
   * Connect to the WASAPI service
   */
  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        resolve();
        return;
      }

      try {
        this.socket = new WebSocket(this.WS_URL);

        this.socket.onopen = () => {
          console.log('Connected to WASAPI service');
          this.connectionStatus.next(true);
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            // Check if we received binary data instead of text
            if (event.data instanceof ArrayBuffer || event.data instanceof Blob) {
              console.error('❌ Received binary data instead of JSON message from WASAPI service');
              return;
            }
            
            this.handleMessage(event.data);
          } catch (msgError) {
            console.error('❌ Error processing WebSocket message:', msgError);
            this.errorSubject.next('Error processing message from WASAPI service');
          }
        };

        this.socket.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.connectionStatus.next(false);
          this.errorSubject.next('Failed to connect to WASAPI service');
          reject(error);
        };

        this.socket.onclose = () => {
          console.log('Disconnected from WASAPI service');
          this.connectionStatus.next(false);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Disconnect from the WASAPI service
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
      this.connectionStatus.next(false);
    }
  }

  /**
   * Check if connected to WASAPI service
   */
  isConnected(): boolean {
    return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  /**
   * Get available audio devices
   */
  async getDevices(): Promise<WasapiDevice[]> {
    return new Promise((resolve, reject) => {
      if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
        reject(new Error('Not connected to WASAPI service'));
        return;
      }

      const command: WasapiCommand = {
        action: 'getdevices',
        token: this.AUTH_TOKEN
      };

      // Listen for devices response
      const subscription = this.devices$.subscribe((devices) => {
        if (devices && devices.length > 0) {
          resolve(devices);
          setTimeout(() => subscription.unsubscribe(), 100);
        }
      });

      this.sendCommand(command);

      // Timeout after 5 seconds
      setTimeout(() => {
        subscription.unsubscribe();
        reject(new Error('Timeout getting devices'));
      }, 5000);
    });
  }

  /**
   * Start recording
   */
  startRecording(deviceId: string, channels: number, sampleRate: number, resample: boolean = false, outputPath?: string): void {
    console.log('🎙️ WASAPI Service: Preparing startrecording command');
    console.log('   Device ID:', deviceId);
    console.log('   Channels:', channels);
    console.log('   Sample Rate:', sampleRate);
    console.log('   Resample:', resample);
    console.log('   WebSocket state:', this.socket?.readyState, '(1=OPEN, 0=CONNECTING, 2=CLOSING, 3=CLOSED)');
    
    // Reset counter
    this.audioDataChunkCount = 0;
    
    const command: WasapiCommand = {
      action: 'startrecording',
      deviceId,
      channels,
      sampleRate,
      resample,
      outputPath,
      token: this.AUTH_TOKEN
    };

    console.log('📤 Sending startrecording command to Windows service:', JSON.stringify(command));
    this.sendCommand(command);
    console.log('✅ Command sent successfully');
  }

  /**
   * Stop recording
   */
  stopRecording(): void {
    const command: WasapiCommand = {
      action: 'stoprecording',
      token: this.AUTH_TOKEN
    };

    this.sendCommand(command);
  }

  /**
   * Get recording status
   */
  getStatus(): void {
    const command: WasapiCommand = {
      action: 'getstatus',
      token: this.AUTH_TOKEN
    };

    this.sendCommand(command);
  }

  /**
   * Send command to the WASAPI service
   */
  private sendCommand(command: WasapiCommand): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
      const errorMsg = 'Not connected to WASAPI service - Cannot send command';
      console.error('❌', errorMsg);
      console.error('   Socket exists:', !!this.socket);
      console.error('   Socket state:', this.socket?.readyState);
      this.errorSubject.next(errorMsg);
      return;
    }

    try {
      const commandStr = JSON.stringify(command);
      console.log('📨 Sending command via WebSocket:', commandStr.substring(0, 100) + '...');
      this.socket.send(commandStr);
      console.log('✅ Command sent to WASAPI Windows service successfully');
    } catch (error) {
      console.error('❌ Error sending command to WASAPI service:', error);
      this.errorSubject.next('Failed to send command');
    }
  }

  /**
   * Handle incoming messages from the WASAPI service
   */
  private handleMessage(data: string): void {
    try {
      const response: WasapiResponse = JSON.parse(data);

      switch (response.type) {
        case 'connected':
          console.log('WASAPI service connected:', response.message);
          break;

        case 'devices':
          if (response.success && response.data) {
            console.log('📡 Received devices from WASAPI:', response.data);
            console.log('📊 Device count:', response.data.length);
            response.data.forEach((device: any, index: number) => {
              console.log(`  [${index}] ${device.name || device.Name} - Channels: ${device.maxChannels || device.MaxChannels}`);
            });
            this.devicesSubject.next(response.data);
          }
          break;

        case 'recordingStarted':
          console.log('🎬 WASAPI service: Recording started!');
          console.log('   Response:', JSON.stringify(response, null, 2));
          if (response.success && response.data) {
            console.log('✅ Recording status data received:', response.data);
            this.recordingStatusSubject.next(response.data);
          } else {
            console.warn('⚠️ recordingStarted event but no data or not successful');
          }
          break;

        case 'recordingStopped':
          console.log('🛑 WASAPI service: Recording stopped');
          console.log('   Response:', JSON.stringify(response, null, 2));
          if (response.data) {
            this.recordingStatusSubject.next(response.data);
          }
          break;

        case 'status':
          if (response.success && response.data) {
            this.recordingStatusSubject.next(response.data);
          }
          break;

        case 'audioData':
          this.audioDataChunkCount++;
          
          // Log first, 10th, 50th, 100th chunks and every 100 after that
          const shouldLog = this.audioDataChunkCount === 1 || 
                           this.audioDataChunkCount === 10 || 
                           this.audioDataChunkCount === 50 || 
                           this.audioDataChunkCount % 100 === 0;
          
          if (shouldLog) {
            console.log(`🎵 WASAPI audioData #${this.audioDataChunkCount} received from Windows service`);
          }
          
          if (response.success && response.data) {
            try {
              // Convert base64 to ArrayBuffer
              const base64Data = response.data as string;
              
              if (!base64Data || base64Data.length === 0) {
                console.error(`❌ Chunk #${this.audioDataChunkCount}: Received empty audio data from WASAPI service`);
                return;
              }
              
              if (this.audioDataChunkCount === 1) {
                console.log(`   First chunk base64 length: ${base64Data.length} characters`);
              }
              
              const binaryString = atob(base64Data);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              
              if (bytes.buffer.byteLength === 0) {
                console.error(`❌ Chunk #${this.audioDataChunkCount}: Converted audio buffer is empty`);
                return;
              }
              
              if (shouldLog) {
                console.log(`   Decoded to ${bytes.buffer.byteLength} bytes`);
              }
              
              // Emit the audio data
              this.audioDataSubject.next(bytes.buffer);
              
              if (this.audioDataChunkCount === 1) {
                console.log('✅ First audio chunk emitted to subscribers successfully!');
              }
            } catch (decodeError) {
              console.error(`❌ Chunk #${this.audioDataChunkCount}: Error decoding base64 audio data:`, decodeError);
              this.errorSubject.next('Failed to decode audio data from WASAPI service');
            }
          } else if (!response.success) {
            console.error('❌ WASAPI service reported audio data error:', response.message);
            this.errorSubject.next(response.message || 'Failed to receive audio data');
          }
          break;

        case 'error':
          console.error('WASAPI service error:', response.message);
          this.errorSubject.next(response.message || 'Unknown error');
          break;

        default:
          console.warn('Unknown message type:', response.type);
      }
    } catch (error) {
      console.error('Error parsing message:', error);
      this.errorSubject.next('Failed to parse message from WASAPI service');
    }
  }

  /**
   * Download the WASAPI service installer
   */
  downloadInstaller(installerUrl: string): void {
    const link = document.createElement('a');
    link.href = installerUrl;
    link.download = 'VIQ_Audio_Capture_Service_Setup.msi';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
}
